import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import { ContactInfoHeader } from '~/modules/artist-roster/components/ArtistContactInfo/components/ContactInfoHeader';
import { ScrollMessage } from '~/modules/artist-roster/components/ArtistContactInfo/components/ScrollMessage';
import { useMinMaxContainerHeight } from '~/modules/artist-roster/components/ArtistContactInfo/hooks/useMinMaxContainerHeight';
import { getArtistContactInfo } from '~/modules/artist-roster/lib/artists';
import type { ArtistEntityType } from '~/modules/artist-roster/types';

import { ContactCards } from './components/ContactCards';
import { ContactsSkeleton } from './components/ContactsSkeleton';
import { NoData } from './components/NoData';
import type { InfoType } from './types';
import { sortContacts } from './utils/sortContacts';

const POPOVER_CLASS = cn(
  'flex w-[600px] flex-col rounded-sm border p-md text-default',
  'shadow-xl drop-shadow-xl bg-level-03 border-emphasis-strong',
);

interface ArtistContactInfoProps {
  partyId: number;
  entityType: ArtistEntityType;
}

export function ArtistContactInfo({
  partyId,
  entityType,
}: ArtistContactInfoProps) {
  const { t } = useTranslation('roster');
  const scrollRef = useRef<HTMLDivElement>(null);
  const { height: scrollHeight } = useMinMaxContainerHeight(scrollRef, {
    maxHeight: 600,
    minHeight: 200,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['artist-contact-info', partyId],
    queryFn: () => getArtistContactInfo({ partyId }),
  });

  let initialSelectedInfoType: InfoType =
    entityType === 'Crossover' ? 'artist' : 'songwriter';

  if (entityType === 'Artist') {
    initialSelectedInfoType = 'artist';
  }

  const [selectedInfoType, setSelectedInfoType] = useState<InfoType>(
    initialSelectedInfoType,
  );
  const artistContacts = data?.artistContacts;
  const songwriterContacts = data?.songwriterContacts;
  const contacts =
    selectedInfoType === 'artist' ? artistContacts : songwriterContacts;

  if (isLoading) {
    return (
      <div className={POPOVER_CLASS} ref={scrollRef}>
        <ContactsSkeleton />
      </div>
    );
  }

  if (!artistContacts?.length && !songwriterContacts?.length) {
    return (
      <div className={POPOVER_CLASS} ref={scrollRef}>
        <div className="flex flex-col items-center justify-center gap-xl rounded-sm py-5xl">
          <span className="flex shrink-0 items-center justify-center">
            <Icon
              type="user-large-slash"
              size="2xl"
              variant="light"
              aria-hidden
              className="text-default"
            />
          </span>
          <span className="text-body-md font-bold text-strong">
            {t('contactInfo.noData.both')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(POPOVER_CLASS, 'overflow-hidden p-0')}>
      <div
        className="overflow-y-auto overscroll-contain p-md"
        ref={scrollRef}
        style={{ maxHeight: scrollHeight }}
      >
        <ContactInfoHeader
          isLoading={isLoading}
          infoType={selectedInfoType}
          onInfoTypeButtonClick={setSelectedInfoType}
          entityType={entityType}
        />
        {!contacts || !contacts?.length ? (
          <NoData infoType={selectedInfoType} />
        ) : (
          <ContactCards contacts={sortContacts(contacts)} />
        )}
      </div>
      <ScrollMessage scrollRef={scrollRef} />
    </div>
  );
}

ArtistContactInfo.displayName = 'ArtistContactInfo';
